(() => {
  const money = amount => window.theme?.Currency?.formatMoney && window.theme?.settings?.moneyFormat
    ? window.theme.Currency.formatMoney(amount, window.theme.settings.moneyFormat)
    : new Intl.NumberFormat(document.documentElement.lang || 'el-GR', { style: 'currency', currency: 'EUR' }).format(amount / 100);
  const requests = new Map();
  const versions = new WeakMap();
  let shippingQueue = Promise.resolve();
  let latestCart = null;
  let revision = 0;
  const requestRate = (root, country, postalCode, signature) => {
    const key = country + ':' + signature;
    if (requests.has(key)) return requests.get(key);
    const promise = shippingQueue.catch(() => {}).then(async () => {
      const base = (root.dataset.root || '/').replace(/\/?$/, '/');
      const params = new URLSearchParams({ 'shipping_address[country]': country, 'shipping_address[zip]': postalCode });
      const options = { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
      const prepared = await fetch(base + 'cart/prepare_shipping_rates.json?' + params, { ...options, method: 'POST' });
      if (!prepared.ok) throw new Error('Shipping rate unavailable');
      for (let attempt = 0; attempt < 12; attempt++) {
        const response = await fetch(base + 'cart/async_shipping_rates.json?' + params, options);
        if (!response.ok) throw new Error('Shipping rate unavailable');
        const data = await response.json();
        if (data && Array.isArray(data.shipping_rates)) {
          const prices = data.shipping_rates.filter(rate => !rate.currency || rate.currency === 'EUR')
            .map(rate => Number(rate.price)).filter(price => Number.isFinite(price) && price >= 0);
          if (!prices.length) throw new Error('No rate for destination');
          return Math.round(Math.min(...prices) * 100);
        }
        await new Promise(resolve => setTimeout(resolve, 400));
      }
      throw new Error('Shipping rate timed out');
    });
    shippingQueue = promise.catch(() => {});
    requests.set(key, promise);
    promise.catch(() => { if (requests.get(key) === promise) requests.delete(key); });
    return promise;
  };
  const renderForm = form => {
    const root = form.querySelector('[data-onyra-shipping]');
    if (!root) return;
    const items = form.querySelector('[data-products] .cart__items');
    const count = latestCart && window.OnyraPackaging
      ? window.OnyraPackaging.eligibleCount(latestCart)
      : Number(items ? items.dataset.count : root.dataset.count) || 0;
    const subtotal = latestCart ? latestCart.total_price : Number(items ? items.dataset.cartTotal : root.dataset.subtotal) || 0;
    const free = count >= 2;
    root.querySelector('[data-onyra-shipping-free]').hidden = !free;
    root.querySelector('[data-onyra-shipping-rates]').hidden = free;
    root.dataset.count = String(count);
    root.dataset.subtotal = String(subtotal);
    const amount = form.querySelector('[data-onyra-checkout-amount]');
    const total = form.querySelector('[data-onyra-checkout-total]');
    if (amount) amount.hidden = !free;
    if (total) total.textContent = money(subtotal);
    const signature = JSON.stringify([revision, count, subtotal]);
    if (root.dataset.quoteKey === signature) return;
    root.dataset.quoteKey = signature;
    const version = (versions.get(root) || 0) + 1;
    versions.set(root, version);
    if (free || count === 0) return;
    for (const [country, postalCode] of [['GR', '10558'], ['CY', '1010']]) {
      const value = root.querySelector('[data-onyra-rate="' + country + '"]');
      value.textContent = '…';
      requestRate(root, country, postalCode, signature).then(price => {
        if (versions.get(root) === version) value.textContent = money(price);
      }).catch(() => {
        if (versions.get(root) === version) value.textContent = '—';
      });
    }
  };
  const renderAll = () => document.querySelectorAll('#CartDrawerForm, #CartPageForm').forEach(renderForm);
  const initSummary = () => {
    document.querySelectorAll('#CartDrawerForm, #CartPageForm').forEach(form => {
      if (form.dataset.onyraSummaryReady) return;
      if (!form.querySelector('[data-onyra-shipping]')) return;
      form.dataset.onyraSummaryReady = 'true';
      const products = form.querySelector('[data-products]');
      if (products) new MutationObserver(() => renderForm(form)).observe(products, { childList: true });
      renderForm(form);
    });
  };
  document.addEventListener('cart:updated', event => {
    latestCart = event.detail?.cart || null;
    revision++;
    requests.clear();
    renderAll();
  });
  const initDrawer = () => {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer || drawer.dataset.onyraViewportReady) return;
    drawer.dataset.onyraViewportReady = 'true';
    const mobile = window.matchMedia('(max-width: 768px)');
    const viewport = window.visualViewport;
    let locked = false;
    let scrollTop = 0;
    let bodyStyle = null;

    const updateViewport = () => {
      if (!locked) return;
      drawer.style.setProperty('--onyra-cart-viewport-height', `${viewport?.height || window.innerHeight}px`);
      drawer.style.setProperty('--onyra-cart-viewport-top', `${viewport?.offsetTop || 0}px`);
    };

    const sync = () => {
      const open = mobile.matches && drawer.classList.contains('drawer--is-open');
      if (open === locked) return;
      locked = open;
      document.documentElement.classList.toggle('onyra-cart-is-open', open);
      if (open) {
        scrollTop = window.scrollY;
        bodyStyle = document.body.getAttribute('style');
        Object.assign(document.body.style, { position: 'fixed', top: `${-scrollTop}px`, left: '0', right: '0', width: '100%' });
        updateViewport();
      } else {
        if (bodyStyle === null) document.body.removeAttribute('style');
        else document.body.setAttribute('style', bodyStyle);
        drawer.style.removeProperty('--onyra-cart-viewport-height');
        drawer.style.removeProperty('--onyra-cart-viewport-top');
        window.scrollTo({ top: scrollTop, left: 0, behavior: 'instant' });
      }
    };

    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    mobile.addEventListener('change', sync);
    window.addEventListener('resize', updateViewport, { passive: true });
    viewport?.addEventListener('resize', updateViewport, { passive: true });
    viewport?.addEventListener('scroll', updateViewport, { passive: true });
    sync();
  };

  const init = () => { initSummary(); initDrawer(); };
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
